import type { Attendance, Person } from "../types";

export type MonthlyExpense = {
  month: string;
  amount: number;
  participantCount: number;
  unmatchedCount: number;
  missingRateCount: number;
};

export type WageDetail = Person & {
  regular: number;
  overtime: number;
  days: number;
  basePay: number;
  overtimePay: number;
  payable: number;
};

export type WageCalculation = {
  rows: WageDetail[];
  unmatchedPersonIds: string[];
  missingRatePersonIds: string[];
  total: number;
};

function money(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function calculateWageDetails(
  attendance: Attendance[],
  people: Person[],
): WageCalculation {
  const peopleById = new Map(people.map(person => [person.id, person]));
  const rowsByPerson = new Map<string, Attendance[]>();
  for (const row of attendance) {
    rowsByPerson.set(row.person_id, [...(rowsByPerson.get(row.person_id) || []), row]);
  }
  const rows: WageDetail[] = [];
  const unmatchedPersonIds: string[] = [];
  const missingRatePersonIds: string[] = [];
  for (const [personId, personRows] of rowsByPerson) {
    const person = peopleById.get(personId);
    if (!person) {
      unmatchedPersonIds.push(personId);
      continue;
    }
    const regular = personRows.reduce((sum, row) => sum + Number(row.regular_hours || 0), 0);
    const overtime = personRows.reduce((sum, row) => sum + Number(row.overtime_hours || 0), 0);
    const days = regular / 8;
    const rate = Number(person.rate || 0);
    const overtimeRate = Number(person.overtime_rate || 0);
    if (rate <= 0) missingRatePersonIds.push(personId);
    const basePay = person.salary_type === "月薪" ? rate : days * rate;
    const overtimePay = overtime * overtimeRate;
    rows.push({
      ...person,
      regular,
      overtime,
      days,
      basePay: money(basePay),
      overtimePay: money(overtimePay),
      payable: money(basePay + overtimePay),
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
  return {
    rows,
    unmatchedPersonIds,
    missingRatePersonIds,
    total: money(rows.reduce((sum, row) => sum + row.payable, 0)),
  };
}

export function calculateExpenseByMonth(
  attendance: Attendance[],
  people: Person[],
): MonthlyExpense[] {
  const rowsByMonthAndPerson = new Map<string, Attendance[]>();

  for (const row of attendance) {
    const month = row.work_date.slice(0, 7);
    const key = `${month}|${row.person_id}`;
    const rows = rowsByMonthAndPerson.get(key) || [];
    rows.push(row);
    rowsByMonthAndPerson.set(key, rows);
  }

  const months = new Map<string, Attendance[]>();
  for (const [key, rows] of rowsByMonthAndPerson) {
    const separator = key.indexOf("|");
    const month = key.slice(0, separator);
    months.set(month, [...(months.get(month) || []), ...rows]);
  }

  return [...months.entries()]
    .map(([month, rows]) => {
      const calculation = calculateWageDetails(rows, people);
      return {
      month,
      amount: calculation.total,
      participantCount: new Set(rows.map(row => row.person_id)).size,
      unmatchedCount: calculation.unmatchedPersonIds.length,
      missingRateCount: calculation.missingRatePersonIds.length,
    };})
    .sort((a, b) => a.month.localeCompare(b.month));
}

export function calculateProjectExpense(
  attendance: Attendance[],
  people: Person[],
) {
  return money(
    calculateExpenseByMonth(attendance, people).reduce(
      (sum, item) => sum + item.amount,
      0,
    ),
  );
}
